import React, { useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartDataset,
  ChartOptions
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { getTestDescription } from '../data/testDescriptions';
import { ReferenceRangeIndicator } from './ReferenceRangeIndicator';
import { DataQualityOverlay } from './DataQualityOverlay';
import { DataQualityDetails } from './DataQualityDetails';
import { getChartDisplayBounds } from '../services/outlierDetectionService';
import { UnitErrorWarning } from './UnitErrorWarning';
import { DataValidationWarning } from './DataValidationWarning';
import { ValidationInfo } from '../types/trends';

// Register Chart.js components including Filler for area fills
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export interface DataPointClickEvent {
  dataIndex: number;
  value: number;
  date: string;
  testName: string;
  units: string;
  referenceMin?: number;
  referenceMax?: number;
  status?: string;
  validationMessage?: string;
}

interface EnhancedLineChartProps {
  title: string;
  testName?: string; // Actual test name for data lookups
  data: {
    labels: string[];
    values: number[];
    referenceMin?: number;
    referenceMax?: number;
    statuses?: ('normal' | 'high' | 'low' | 'unknown')[];
    mixedUnits?: boolean;
    reportIds?: number[]; // Array of report IDs corresponding to each data point
    resultIds?: number[]; // Array of result IDs corresponding to each data point
    referenceSource?: 'original' | 'enhanced' | 'none' | 'standard' | 'lab'; // Source of reference range
    hasUnitConversions?: boolean; // True if values were converted
    originalValues?: number[]; // Original values before conversion
    originalUnits?: string[]; // Original units before conversion
    suspiciousFlags?: boolean[]; // Flags for potentially erroneous data points
    validationMessages?: string[]; // LLM validation messages for suspicious points
    outlierConfidences?: ('low' | 'medium' | 'high')[]; // Confidence levels for outliers
    displayValues?: number[]; // Values to display (may be capped for extreme outliers)
    unitError?: {
      isLikelyError: boolean;
      suggestedUnit?: string;
      reason?: string;
    };
  };
  units: string;
  dataPoints?: Array<{
    date: string;
    value: number;
    units: string;
    referenceMin?: number | null;
    referenceMax?: number | null;
    referenceSource?: string; // Source for this specific data point
    status?: string;
    autoFixed?: boolean;
    originalValue?: number;
    originalUnits?: string;
    chartValue?: number; // Value to use for chart display
    isOutlier?: boolean;
    outlierConfidence?: 'low' | 'medium' | 'high';
    outlierReason?: string;
    validation?: ValidationInfo; // LLM validation results
  }>;
  onDataPointClick?: (event: DataPointClickEvent, position: { x: number; y: number }) => void;
  onAcceptValidation?: (dataIndex: number, value: number, unit: string) => void;
  onDismissValidation?: (dataIndex: number) => void;
  onRequestReferenceRange?: (testName: string) => void;
  isLoadingReference?: boolean;
  referenceSourceDetails?: { source?: string; notes?: string };
  showReferenceRange?: boolean;
  referenceRangeSource?: 'lab' | 'standard';
}

const EnhancedLineChart: React.FC<EnhancedLineChartProps> = ({ 
  title,
  testName,
  data, 
  units, 
  dataPoints,
  onDataPointClick,
  onAcceptValidation,
  onDismissValidation,
  onRequestReferenceRange,
  isLoadingReference,
  referenceSourceDetails,
  showReferenceRange = true,
  referenceRangeSource = 'standard'
}) => {
  const [showDataQualityDetails, setShowDataQualityDetails] = useState(false);
  const [showAutoFixedAlert, setShowAutoFixedAlert] = useState(true);
  const [showUnitIssuesAlert, setShowUnitIssuesAlert] = useState(true);
  const [dismissedValidations, setDismissedValidations] = useState<Set<string>>(new Set());
  const { labels, values, referenceMin, referenceMax, statuses, suspiciousFlags, validationMessages, 
          referenceSource = 'none', displayValues, outlierConfidences, unitError } = data;
  
  // Use display values if provided (for extreme outliers), otherwise use regular values
  const chartValues = displayValues || values;
  
  // Helper function to check if zero is valid for a test
  const isZeroValidForTest = (testName: string): boolean => {
    const validZeroTests = [
      'BASOPHIL', 'EOSINOPHIL', 'RETICULOCYTE', 'RETIC',
      'IGE', 'IGG', 'IGM', 'IGA', 'ANTIBODY', 'TITER',
      'CEA', 'AFP', 'CA125', 'CA19-9', 'PSA',
      'ETHANOL', 'ALCOHOL', 'DRUG', 'COCAINE', 'MARIJUANA', 'THC',
      'HCG', 'BHCG', 'PROGESTERONE', 'ESTRADIOL'
    ];
    
    const testUpper = testName.toUpperCase();
    return validZeroTests.some(validTest => 
      testUpper.includes(validTest) || validTest.includes(testUpper)
    );
  };
  
  // Detect data quality issues - zero values are errors for some lab tests
  const hasZeroValues = values.some(v => v === 0) && !isZeroValidForTest(title);
  
  // Check for incorrect reference ranges
  let hasIncorrectReferenceRange = false;
  let referenceRangeError = '';
  let correctReferenceRange: { min: number; max: number } | undefined;
  
  if (referenceMin !== undefined && referenceMax !== undefined) {
    const titleLower = title.toLowerCase();
    
    if (titleLower.includes('mchc')) {
      if (units === 'g/dL' && (referenceMin > 100 || referenceMax > 100)) {
        hasIncorrectReferenceRange = true;
        referenceRangeError = `MCHC reference range (${referenceMin}-${referenceMax} g/dL) appears to be in wrong units`;
        correctReferenceRange = { min: 32, max: 36 };
      }
    } else if (titleLower.includes('hemoglobin')) {
      if (units === 'g/dL' && (referenceMin > 30 || referenceMax > 30)) {
        hasIncorrectReferenceRange = true;
        referenceRangeError = `Hemoglobin reference range (${referenceMin}-${referenceMax} g/dL) appears to be in wrong units`;
        correctReferenceRange = { min: 12, max: 17 };
      }
    }
  }
  
  // Debug logging
  console.log(`EnhancedLineChart for ${title}:`, {
    hasReferenceMin: referenceMin !== undefined && referenceMin !== null,
    hasReferenceMax: referenceMax !== undefined && referenceMax !== null,
    referenceMin,
    referenceMax,
    showReferenceRange,
    dataPoints: values.length,
    hasZeroValues,
    hasIncorrectReferenceRange
  });
  
  // Check if value is critically low for specific tests
  const isCriticallyLow = (value: number, index: number): boolean => {
    const titleLower = title.toLowerCase();
    
    // Check if zero value is valid for this test
    if (value === 0) {
      return !isZeroValidForTest(title);
    }
    
    if (titleLower.includes('mchc') && units === 'g/dL' && value < 20) return true;
    if ((titleLower.includes('hemoglobin') || titleLower.includes('haemoglobin')) && 
        units === 'g/dL' && value < 5) return true;
    return false;
  };
  
  // Determine point colors based on status and data quality
  const pointBackgroundColors = statuses?.map((status, index) => {
    // If value is zero or critically low (error), use bright red
    if (isCriticallyLow(values[index], index)) {
      return '#dc2626'; // bright red for zero/critical values
    }
    
    // If value was auto-fixed, use purple
    if (dataPoints?.[index]?.autoFixed) {
      return '#9333ea'; // purple for auto-fixed values
    }
    
    // If point has validation issues, use yellow/orange based on confidence
    if (dataPoints?.[index]?.validation && !dataPoints[index].validation?.isValid) {
      const confidence = dataPoints[index].validation?.confidence || 0;
      if (confidence > 0.8) {
        return '#dc2626'; // high confidence error - red
      } else if (confidence > 0.5) {
        return '#f97316'; // medium confidence - orange
      } else {
        return '#fbbf24'; // low confidence - yellow
      }
    }
    
    // If point is suspicious, use orange with a warning pattern
    if (suspiciousFlags?.[index]) {
      return '#f97316'; // orange for suspicious data
    }
    
    switch (status) {
      case 'high': return '#ef4444'; // red
      case 'low': return '#3b82f6'; // blue
      case 'normal': return '#10b981'; // green
      default: return '#6b7280'; // gray
    }
  }) || '#10b981';
  
  // Add point border style for problematic points
  const pointBorderWidths = values.map((value, index) => {
    if (isCriticallyLow(value, index)) return 4; // Thick border for critical values
    if (dataPoints?.[index]?.validation && !dataPoints[index].validation?.isValid) return 4; // Thick border for validation issues
    if (suspiciousFlags?.[index]) return 4; // Thick border for suspicious
    return 2; // Normal border
  });
  
  const pointRadii = values.map((value, index) => {
    if (isCriticallyLow(value, index)) return 14; // Extra large for critical values to ensure visibility
    if (dataPoints?.[index]?.validation && !dataPoints[index].validation?.isValid) return 12; // Larger for validation issues
    if (suspiciousFlags?.[index]) return 10; // Larger for suspicious
    if (dataPoints?.[index]?.autoFixed) return 10; // Larger for auto-fixed values
    return 6; // Normal size
  });
  
  // Use different point styles for problematic data
  const pointStyles = values.map((value, index) => {
    if (isCriticallyLow(value, index)) {
      return 'crossRot'; // X mark for critically low values
    }
    if (dataPoints?.[index]?.validation && !dataPoints[index].validation?.isValid) {
      return 'rectRounded'; // Rounded rectangle for validation issues
    }
    if (suspiciousFlags?.[index]) {
      return 'triangle'; // Triangle for suspicious values
    }
    return 'circle'; // Default circle
  });
  
  // Check if we have unit conversions or mislabeled units
  const hasUnitIssues = data.hasUnitConversions || 
    (data.originalUnits && data.originalUnits.some((unit, idx) => unit !== units));

  // Determine if we should show reference range
  const hasReferenceRange = referenceMin !== undefined && referenceMax !== undefined && 
    referenceMin !== null && referenceMax !== null;
  
  // Check if this test has no practical upper limit (e.g., HDL cholesterol)
  const hasNoUpperLimit = referenceMax === 999 || (referenceMin !== null && referenceMax === null);
  
  // For display purposes, cap the reference max at a reasonable value
  const displayReferenceMax = hasNoUpperLimit && values.length > 0 
    ? Math.max(...values) * 1.5 
    : referenceMax;
  
  // Create datasets for the chart
  const datasets: ChartDataset<'line'>[] = [];

  // Debug reference range rendering
  if (title.toLowerCase().includes('hdl')) {
    console.log(`📊 HDL Chart Rendering:`, {
      hasReferenceRange,
      showReferenceRange,
      referenceMin,
      referenceMax,
      hasNoUpperLimit,
      values
    });
  }
  
  // Add reference range if enabled and available
  if ((hasReferenceRange || hasNoUpperLimit) && showReferenceRange && referenceMin !== null) {
    if (hasNoUpperLimit) {
      // For tests with no upper limit (like HDL), show shaded area from minimum upward
      const maxDataValue = Math.max(...values);
      const minDataValue = Math.min(...values);
      const dataRange = maxDataValue - minDataValue;
      
      // Calculate a reasonable upper bound for the shaded area
      let upperBound: number;
      if (dataRange < maxDataValue * 0.1) {
        // If values are tightly clustered, use 30% padding
        upperBound = maxDataValue * 1.3;
      } else {
        // Otherwise add 30% of the range
        upperBound = maxDataValue + (dataRange * 0.3);
      }
      
      // Add upper bound for shaded area with gradient
      datasets.push({
        label: 'Normal Range (Upper)',
        data: labels.map(() => upperBound),
        borderColor: 'transparent',
        backgroundColor: (context: any) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, context.chart.height);
          gradient.addColorStop(0, 'rgba(34, 197, 94, 0.08)');
          gradient.addColorStop(0.7, 'rgba(34, 197, 94, 0.15)');
          gradient.addColorStop(1, 'rgba(34, 197, 94, 0.18)');
          return gradient;
        },
        borderWidth: 0,
        pointRadius: 0,
        pointHoverRadius: 0,
        fill: '+1', // Fill down to the minimum line
        order: 2
      });
      
      // Add minimum line with better visibility
      datasets.push({
        label: 'Minimum Recommended',
        data: labels.map(() => referenceMin!),
        borderColor: 'rgba(34, 197, 94, 0.6)',
        borderDash: [10, 5],
        borderWidth: 2.5,
        backgroundColor: 'transparent',
        pointRadius: 0,
        pointHoverRadius: 0,
        fill: false,
        order: 2
      });
    } else {
      // Regular reference range with shaded area
      // Add subtle top border line
      datasets.push({
        label: 'Reference Max',
        data: labels.map(() => displayReferenceMax ?? referenceMax),
        borderColor: 'rgba(34, 197, 94, 0.4)', // Slightly more visible green border
        backgroundColor: 'transparent',
        borderWidth: 2,
        borderDash: [10, 5], // More prominent dash pattern
        pointRadius: 0,
        pointHoverRadius: 0,
        fill: false,
        order: 2
      });
      
      // Add the filled area with gradient
      datasets.push({
        label: 'Normal Range',
        data: labels.map(() => displayReferenceMax ?? referenceMax),
        borderColor: 'transparent',
        backgroundColor: (context: any) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, context.chart.height);
          gradient.addColorStop(0, 'rgba(34, 197, 94, 0.12)');
          gradient.addColorStop(0.5, 'rgba(34, 197, 94, 0.18)');
          gradient.addColorStop(1, 'rgba(34, 197, 94, 0.12)');
          return gradient;
        },
        borderWidth: 0,
        pointRadius: 0,
        pointHoverRadius: 0,
        fill: {
          target: {
            value: referenceMin
          }
        },
        order: 3 // Behind everything
      });
      
      // Add subtle bottom border line
      datasets.push({
        label: 'Reference Min',
        data: labels.map(() => referenceMin!),
        borderColor: 'rgba(34, 197, 94, 0.4)', // Slightly more visible green border
        backgroundColor: 'transparent',
        borderWidth: 2,
        borderDash: [10, 5], // More prominent dash pattern
        pointRadius: 0,
        pointHoverRadius: 0,
        fill: false,
        order: 2
      });
    }
  }

  // Add the main data line
  datasets.push({
    label: title,
    data: chartValues, // Use display values for extreme outliers
    borderColor: '#6366f1', // indigo
    backgroundColor: 'rgba(99, 102, 241, 0.1)',
    borderWidth: 2.5,
    pointRadius: pointRadii, // Larger for suspicious points
    pointHoverRadius: 10, // Even larger on hover
    pointHitRadius: 15, // Larger hit area for clicks
    pointBackgroundColor: pointBackgroundColors,
    pointBorderColor: '#fff',
    pointBorderWidth: pointBorderWidths, // Thicker border for suspicious points
    pointStyle: pointStyles, // Different shapes for problematic points
    tension: 0.3, // Smooth curves
    fill: false,
    order: 1 // Render on top of reference range
  });
  
  const chartData = {
    labels,
    datasets,
  };
  
  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'nearest' as const,
      intersect: true,
    },
    onClick: (event, elements, chart) => {
      if (elements.length > 0 && onDataPointClick) {
        // Find the element that belongs to the main data line (not reference range)
        const dataElement = elements.find(el => {
          const dataset = chart.data.datasets[el.datasetIndex];
          return dataset.label === title; // Main data line has the test name as label
        });
        
        if (!dataElement) {
          return;
        }
        
        const dataIndex = dataElement.index;
        const value = values[dataIndex];
        const date = labels[dataIndex];
        const status = statuses?.[dataIndex];
        
        // Get the canvas position for the clicked point
        const canvasPosition = chart.canvas.getBoundingClientRect();
        const meta = chart.getDatasetMeta(dataElement.datasetIndex);
        const point = meta.data[dataIndex];
        const x = canvasPosition.left + (point as any).x;
        const y = canvasPosition.top + (point as any).y;
        
        onDataPointClick({
          dataIndex,
          value,
          date,
          testName: testName || title, // Use actual test name if provided
          units,
          referenceMin,
          referenceMax,
          status
        }, { x, y });
      }
    },
    plugins: {
      legend: {
        display: false, // We'll create custom legend
      },
      title: {
        display: false, // We'll create custom title
      },
      filler: {
        propagate: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#374151',
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
        displayColors: false,
        filter: function(tooltipItem) {
          // Only show the main data line in tooltip
          const label = tooltipItem.dataset.label;
          return label === title; // Only show the test data, not reference ranges
        },
        enabled: true,
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          title: function(tooltipItems) {
            if (!tooltipItems || tooltipItems.length === 0) {
              return '';
            }
            return tooltipItems[0]?.label || '';
          },
          label: function(context) {
            if (!context || context.dataset.label === 'Normal Range') {
              return undefined;
            }
            
            const displayValue = context.parsed.y;
            const actualValue = values[context.dataIndex]; // Get actual value
            
            if (displayValue === null || displayValue === undefined) {
              return undefined;
            }
            
            // Check if value was capped for display
            const wasCapped = displayValues && displayValues[context.dataIndex] !== values[context.dataIndex];
            
            // Format value to reasonable precision (max 2 decimal places)
            let formattedValue = Number(actualValue.toFixed(2));
            let label = `${formattedValue} ${units}`;
            
            // Add indicator if value was capped
            if (wasCapped) {
              label = `${formattedValue} ${units} (extreme outlier)`;
            }
            
            const status = statuses?.[context.dataIndex] || 'unknown';
            const statusEmoji = {
              high: '⬆️',
              low: '⬇️',
              normal: '✓',
              unknown: '?'
            }[status] || '';  // Default to empty string instead of undefined
            
            // Only add emoji if we have one
            return statusEmoji ? `${label} ${statusEmoji}` : label;
          },
          afterLabel: function(context) {
            if (!context || !context.dataset) {
              return undefined;
            }
            
            if (context.dataset.label === 'Normal Range' || 
                context.dataset.label === 'Reference Max' || 
                context.dataset.label === 'Reference Min') {
              return undefined;
            }
            
            // Add warning for suspicious data points
            const dataIndex = context.dataIndex;
            if (dataIndex === undefined || dataIndex === null) {
              return undefined;
            }
            
            // Check for validation issues first
            if (dataPoints?.[dataIndex]?.validation && !dataPoints[dataIndex].validation?.isValid) {
              const validation = dataPoints[dataIndex].validation!;
              const warningLines = [];
              warningLines.push('⚠️ Data Quality Issue Detected');
              if (validation.issues && validation.issues.length > 0) {
                warningLines.push(...validation.issues.map(issue => `  • ${issue}`));
              }
              if (validation.suggestedValue !== undefined) {
                warningLines.push(`💡 Suggested: ${validation.suggestedValue} ${validation.suggestedUnit || units}`);
              }
              return warningLines;
            }
            
            if (suspiciousFlags?.[dataIndex]) {
              // Use specific validation message if available
              if (validationMessages?.[dataIndex]) {
                return `⚠️ ${validationMessages[dataIndex]}`;
              }
              return '⚠️ Unusually large change from previous value';
            }
            
            const lines = [];
            
            // Show original value if converted
            if (data.hasUnitConversions && data.originalValues && data.originalUnits) {
              const originalValue = data.originalValues[dataIndex];
              const originalUnit = data.originalUnits[dataIndex];
              const currentValue = context.parsed.y;
              
              if (originalValue !== currentValue) {
                const formattedOriginal = Number(originalValue.toFixed(2));
                lines.push(`Original: ${formattedOriginal} ${originalUnit}`);
                lines.push(''); // Empty line
              }
            }
            
            if (hasReferenceRange && referenceMin !== undefined && referenceMax !== undefined && 
                referenceMin !== null && referenceMax !== null) {
              const formattedMin = Number(referenceMin.toFixed(2));
              const formattedMax = hasNoUpperLimit ? '∞' : Number(referenceMax.toFixed(2));
              lines.push(`Normal: ${formattedMin}${hasNoUpperLimit ? '+' : '-' + formattedMax} ${units}`);
            }
            if (onDataPointClick) {
              if (lines.length > 0) {
                lines.push(''); // Empty line only if we have content above
              }
              lines.push('📝 Click to edit value');
            }
            return lines.length > 0 ? lines : undefined;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 12,
          },
          maxRotation: 45,
          minRotation: 45,
        }
      },
      y: {
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          font: {
            size: 12,
          },
          callback: function(value) {
            // Format y-axis labels to reasonable precision
            const formattedValue = Number(Number(value).toFixed(2));
            return formattedValue + ' ' + units;
          }
        },
        // Use intelligent bounds that exclude extreme outliers
        min: (() => {
          // If we have enhanced data points with outlier info, use smart bounds
          if (dataPoints && dataPoints.length > 0) {
            const bounds = getChartDisplayBounds(
              dataPoints as any, // Type assertion needed
              referenceMin,
              referenceMax
            );
            return bounds.min;
          }
          // Fallback to reference range bounds if available
          if (hasReferenceRange && referenceMin !== undefined) {
            const dataMin = Math.min(...chartValues);
            return Math.min(dataMin * 0.9, referenceMin * 0.85);
          }
          return undefined; // Let Chart.js auto-scale
        })(),
        max: (() => {
          // If we have enhanced data points with outlier info, use smart bounds
          if (dataPoints && dataPoints.length > 0) {
            const bounds = getChartDisplayBounds(
              dataPoints as any, // Type assertion needed
              referenceMin,
              referenceMax
            );
            return bounds.max;
          }
          
          // For tests with no upper limit (like HDL), calculate reasonable max
          if (hasNoUpperLimit && values.length > 0) {
            const dataMax = Math.max(...chartValues);
            const dataMin = Math.min(...chartValues);
            const dataRange = dataMax - dataMin;
            
            // If all values are similar, use a sensible range
            if (dataRange < dataMax * 0.1) {
              return dataMax * 1.3; // 30% padding for tight data
            }
            
            // Otherwise scale based on the range
            return dataMax + (dataRange * 0.3); // 30% of range as padding
          }
          
          // Fallback to reference range bounds if available
          if (hasReferenceRange && displayReferenceMax !== undefined) {
            const dataMax = Math.max(...chartValues);
            return Math.max(dataMax * 1.1, displayReferenceMax * 1.15);
          }
          return undefined;
        })(),
      }
    }
  };
  
  return (
    <div className="w-full">
      {/* Unit Error Warning if applicable */}
      {unitError?.isLikelyError && !hasReferenceRange && (
        <UnitErrorWarning
          testName={title}
          currentUnit={units}
          suggestedUnit={unitError.suggestedUnit}
          reason={unitError.reason}
        />
      )}
      
      {/* Using standard ranges notification when lab ranges were requested but not available */}
      {showReferenceRange && referenceRangeSource === 'lab' && 
       data.referenceSource === 'standard' && hasReferenceRange && (
        <div className="mb-2 text-xs text-gray-500 flex items-center gap-1">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Using standard medical ranges (lab ranges not available)</span>
        </div>
      )}
      
      {/* Custom Header */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <div className="group relative">
            <button
              className="p-1 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Test information"
            >
              <svg className="w-4 h-4 text-gray-400 group-hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
            {/* Tooltip */}
            <div className="absolute left-0 bottom-full mb-2 w-64 p-3 bg-gray-800 text-white text-sm rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
              <div className="font-medium mb-1">{title}</div>
              <div className="text-gray-300">{getTestDescription(title)}</div>
              <div className="absolute bottom-0 left-4 transform translate-y-1/2 rotate-45 w-2 h-2 bg-gray-800"></div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Normal Range Indicator */}
          {hasReferenceRange && showReferenceRange && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <div className="w-3 h-3 bg-green-500/20 border border-green-500/30 rounded-sm"></div>
              <span>Normal Range</span>
            </div>
          )}
          
          {/* Reference Range Indicator */}
          <ReferenceRangeIndicator 
            source={hasReferenceRange ? referenceSource : 'none'}
            isLoading={isLoadingReference}
            onRequestEnhancement={
              (!hasReferenceRange && onRequestReferenceRange) 
                ? () => onRequestReferenceRange(title) 
                : undefined
            }
            sourceDetails={referenceSourceDetails}
          />
          <button
            onClick={() => {
              // Export data as CSV
              const csv = [
                ['Date', 'Value', 'Status', 'Reference Min', 'Reference Max'],
                ...labels.map((label, i) => [
                  label,
                  values[i],
                  statuses?.[i] || 'unknown',
                  referenceMin || '',
                  referenceMax || ''
                ])
              ].map(row => row.join(',')).join('\n');
              
              const blob = new Blob([csv], { type: 'text/csv' });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `${title.replace(/\s+/g, '_')}_trends.csv`;
              a.click();
              window.URL.revokeObjectURL(url);
            }}
            className="text-sm px-3 py-1 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
            title="Export as CSV"
          >
            ↓ Export
          </button>
        </div>
      </div>
      
      {/* Chart Container */}
      <div className="relative h-64">
        {/* Data Quality Overlay */}
        <DataQualityOverlay
          hasZeroValues={hasZeroValues}
          hasIncorrectReferenceRange={hasIncorrectReferenceRange}
          referenceRangeError={referenceRangeError}
          correctReferenceRange={correctReferenceRange}
          onFixReferenceRange={hasIncorrectReferenceRange && onRequestReferenceRange ? 
            () => onRequestReferenceRange(title) : undefined}
        />
        <Line options={options} data={chartData} />
      </div>
      
      
      {/* Stats Summary */}
      <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
        <div className="text-center p-2 bg-gray-50 rounded">
          <div className="text-gray-500">Latest</div>
          <div className="font-semibold">{Number(values[values.length - 1].toFixed(2))} {units}</div>
        </div>
        <div className="text-center p-2 bg-gray-50 rounded">
          <div className="text-gray-500">Average</div>
          <div className="font-semibold">
            {Number((values.reduce((a, b) => a + b, 0) / values.length).toFixed(2))} {units}
          </div>
        </div>
        <div className="text-center p-2 bg-gray-50 rounded">
          <div className="text-gray-500">Trend</div>
          <div className="font-semibold">
            {values[values.length - 1] > values[0] ? '↑' : values[values.length - 1] < values[0] ? '↓' : '→'}
            {' '}
            {Math.abs(((values[values.length - 1] - values[0]) / values[0]) * 100).toFixed(1)}%
          </div>
        </div>
      </div>
      
      {/* Note about no upper limit */}
      {hasNoUpperLimit && showReferenceRange && referenceMin !== null && (
        <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-700">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <span className="font-medium">Higher is better:</span> {title} should be above {referenceMin} {units}
              <span className="block text-green-600 mt-0.5">The green shaded area shows the healthy range</span>
            </div>
          </div>
        </div>
      )}
      
      {/* Auto-fixed values notice */}
      {dataPoints?.some(dp => dp.autoFixed) && showAutoFixedAlert && (
        <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
          <div className="flex items-start gap-2">
            <svg className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm text-purple-800 font-medium">Unit Errors Auto-Corrected</p>
                  <div className="mt-1 text-xs text-purple-700">
                    <p>Purple points indicate values that were automatically corrected due to obvious unit errors.</p>
                    {dataPoints.filter(dp => dp.autoFixed).map((dp, idx) => {
                      const wasConverted = dp.originalValue !== dp.value;
                      return (
                        <div key={idx} className="mt-1">
                          • {new Date(dp.date).toLocaleDateString()}: {
                            wasConverted 
                              ? `Converted ${dp.originalValue} ${dp.originalUnits} → ${dp.value.toFixed(1)} ${dp.units}`
                              : `Corrected label from ${dp.originalUnits} to ${dp.units}`
                          }
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => {
                      // Trigger review dialog - need to pass this up to parent
                      if (window.reviewAutoCorrections) {
                        window.reviewAutoCorrections(title);
                      }
                    }}
                    className="text-xs bg-purple-600 text-white px-3 py-1 rounded hover:bg-purple-700 flex-shrink-0"
                  >
                    Review
                  </button>
                  <button
                    onClick={() => setShowAutoFixedAlert(false)}
                    className="text-purple-600 hover:text-purple-800 flex-shrink-0"
                    title="Dismiss"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Unit Notice - show if mixed units were detected */}
      {hasUnitIssues && showUnitIssuesAlert && (
        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start">
            <svg className="h-5 w-5 text-amber-600 mt-0.5 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-amber-800">Unit Normalization Applied</h4>
                  <p className="text-xs text-amber-700 mt-1">
                    Some values were reported with different units and have been converted to {units}.
                  </p>
                </div>
                <button
                  onClick={() => setShowUnitIssuesAlert(false)}
                  className="ml-4 text-amber-600 hover:text-amber-800 flex-shrink-0"
                  title="Dismiss"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              {/* Show details of conversions */}
              {data.originalValues && data.originalUnits && (
                <details className="mt-2">
                  <summary className="text-xs text-amber-600 cursor-pointer hover:text-amber-700">
                    View original values
                  </summary>
                  <div className="mt-2 text-xs text-amber-600 space-y-1 max-h-32 overflow-y-auto">
                    {data.originalValues.map((val, idx) => {
                      if (data.originalUnits?.[idx] !== units) {
                        return (
                          <div key={idx} className="flex items-center gap-2">
                            <span className="text-gray-500">{labels[idx]}:</span>
                            <span className="font-mono">
                              {val} {data.originalUnits?.[idx]} → {values[idx].toFixed(2)} {units}
                            </span>
                          </div>
                        );
                      }
                      return null;
                    }).filter(Boolean)}
                  </div>
                </details>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Data Quality Details Modal */}
      {showDataQualityDetails && dataPoints && (
        <DataQualityDetails
          testName={title}
          dataPoints={dataPoints}
          onClose={() => setShowDataQualityDetails(false)}
        />
      )}
      
      {/* Validation Warnings - Show for outliers with high confidence */}
      {dataPoints && (() => {
        // Find the most critical issue - either from validation or outlier detection
        const criticalIssues = dataPoints
          .map((dp, idx) => ({ dp, idx }))
          .filter(({ dp }) => {
            // Include points that are:
            // 1. Marked as outliers with high confidence
            // 2. Have validation issues with high confidence
            const isHighConfidenceOutlier = dp.isOutlier && dp.outlierConfidence === 'high';
            const hasValidationIssue = dp.validation && !dp.validation.isValid && dp.validation.confidence > 0.7;
            return isHighConfidenceOutlier || hasValidationIssue;
          })
          .sort((a, b) => {
            // Prioritize by value magnitude to catch the most extreme outliers first
            const aValue = a.dp.value;
            const bValue = b.dp.value;
            const aMagnitude = Math.abs(aValue);
            const bMagnitude = Math.abs(bValue);
            
            // For the same test, larger values are often the outliers
            return bMagnitude - aMagnitude;
          });
        
        if (criticalIssues.length === 0) return null;
        
        // Get the most critical issue (highest value for potential outliers)
        const criticalIssue = criticalIssues[0];
        const { dp, idx } = criticalIssue;
        const validationKey = `${dp.date}-${dp.value}`;
        
        if (dismissedValidations.has(validationKey)) return null;
        
        // Create a validation object if we only have outlier info
        const validation = dp.validation || {
          isValid: false,
          confidence: dp.outlierConfidence === 'high' ? 0.9 : 0.7,
          issues: [dp.outlierReason || 'Value appears to be an extreme outlier'],
          explanation: dp.outlierReason || 'This value is significantly different from your other measurements',
          suggestedValue: undefined,
          suggestedUnit: undefined
        };
        
        return (
          <DataValidationWarning
            key={validationKey}
            testName={title}
            currentValue={dp.value}
            currentUnit={dp.units}
            validation={validation}
            onAcceptSuggestion={(suggestedValue, suggestedUnit) => {
              // Instead of opening the editor, directly apply the suggestion
              if (onDataPointClick && onAcceptValidation) {
                // Call a new handler specifically for accepting validations
                onAcceptValidation(idx, suggestedValue, suggestedUnit);
              } else if (onDataPointClick) {
                // Fallback to opening editor if no direct handler
                onDataPointClick({
                  dataIndex: idx,
                  value: suggestedValue,
                  date: dp.date,
                  testName: testName || title,
                  units: suggestedUnit,
                  referenceMin: dp.referenceMin || undefined,
                  referenceMax: dp.referenceMax || undefined,
                  status: dp.status,
                  validationMessage: validation.explanation
                }, { 
                  x: window.innerWidth / 2,
                  y: window.innerHeight / 2 
                });
              }
            }}
            onDismiss={() => {
              setDismissedValidations(prev => new Set(prev).add(validationKey));
              // Also clear the validation from the data point
              if (onDismissValidation) {
                onDismissValidation(idx);
              }
            }}
          />
        );
      })()}
    </div>
  );
};

export default EnhancedLineChart;